// Single-source 401 handler for every redux-api-middleware FAILURE.
//
// Any RSAA call (`createAction({...endpoint})`) whose response status
// is 401 means the server-side session for `app()->user` is gone (the
// `Controller::requireAuth()` helper throws an `HttpException(401)`
// before proxying upstream). Whatever the bundle is — bidding wizard,
// account cabinet, auth SPA, header — the right move is the same:
// bounce the customer to `/customer/?returnUrl=<current>` so they
// can sign in and resume exactly where they were.
//
// 403 is intentionally NOT caught here. A 403 from the upstream proxy
// means "authenticated, not your resource" (IDOR guard on someone
// else's order, etc.); redirecting that to login would lie about
// the cause and trap the customer in a login loop. Components keep
// their existing 403-surfacing behavior (error toasts).
//
// `returnUrl` lives in the URL on the redirect because:
//   · the redirect happens from the BROWSER, with no PHP session
//     reachable to stash it server-side
//   · `_engine.php` cookies are HTTP-only on a host with a port, so
//     a "set session, then GET /customer/" round-trip is fragile in
//     local dev (different scheme / SameSite mismatch)
// Customer-side `customer.php` validates the value against an
// absolute-path-only allowlist before honoring it.

import {buildLoginUrl} from '@common/helpers/loginRedirect';

function hasGuestStatus(action) {
    if (!action || typeof action !== 'object') return false;
    // `redux-api-middleware` puts the upstream status on `meta.status`
    // for FAILURE actions and on `payload.status` when the payload is
    // an `ApiError`. Either path is treated as the same signal.
    const metaStatus = action.meta && action.meta.status;
    const payloadStatus = action.payload && action.payload.status;
    return metaStatus === 401 || payloadStatus === 401;
}

// Re-entry guard. A single FAILURE can trigger several reducers (and
// each component's `useEffect(error)` may dispatch follow-up actions);
// we only ever want one `location.assign` per page.
let redirecting = false;

// Factory matching the rest of the chain (`createMiddleware()`,
// `createErrorReporterMiddleware()` are also called with `()` in
// `configureStore`). Returns the full 3-curried redux middleware:
// `api => next => action => result`. `applyMiddleware` passes the
// store API as the first arg, even though we don't need it.
const authRedirectMiddleware = () => () => (next) => (action) => {
    if (!redirecting && hasGuestStatus(action)) {
        redirecting = true;
        window.location.assign(buildLoginUrl());
    }
    return next(action);
};

export default authRedirectMiddleware;
